import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data", "loading", "refetch", "fetchMore", "error"];
import { registerQuery, useQuery } from 'data-fetching-client';
import { useCallback, useMemo } from 'react';
import { mergeById } from '../utils/mergeById';
import { api } from '../utils/api';
import { TICKET_THREAD_VIEW_BASE_PATH } from '../../api/paths';
export const GET_TICKET_THREAD_KEY = 'getTicketThread';
const threadIdToState = new Map();
export const GET_TICKET_THREAD_QUERY_POLICY = {
  [GET_TICKET_THREAD_KEY]: {
    keyArgs: ['ticketId', 'threadId'],
    merge: (existing, incoming) => {
      var _existing$pagedThread, _incoming$pagedThread, _existing$pagedThread2, _existing$pagedThread3, _incoming$pagedThread2, _incoming$pagedThread3;
      if (!existing) {
        return incoming;
      }
      const mergedResults = mergeById(((_existing$pagedThread = existing.pagedThreadHistory) === null || _existing$pagedThread === void 0 ? void 0 : _existing$pagedThread.results) || [], ((_incoming$pagedThread = incoming.pagedThreadHistory) === null || _incoming$pagedThread === void 0 ? void 0 : _incoming$pagedThread.results) || []);
      const allResultsLoaded = ((_existing$pagedThread2 = existing.pagedThreadHistory) === null || _existing$pagedThread2 === void 0 ? void 0 : _existing$pagedThread2.hasMore) === false;
      return {
        attachmentsMap: Object.assign({}, existing.attachmentsMap, incoming.attachmentsMap),
        responders: Object.assign({}, existing.responders, incoming.responders),
        recipients: Object.assign({}, existing.recipients, incoming.recipients),
        pagedThreadHistory: Object.assign({}, existing.pagedThreadHistory, incoming.pagedThreadHistory, {
          offset: allResultsLoaded ? (_existing$pagedThread3 = existing.pagedThreadHistory) === null || _existing$pagedThread3 === void 0 ? void 0 : _existing$pagedThread3.offset : (_incoming$pagedThread2 = incoming.pagedThreadHistory) === null || _incoming$pagedThread2 === void 0 ? void 0 : _incoming$pagedThread2.offset,
          hasMore: allResultsLoaded ? false : (_incoming$pagedThread3 = incoming.pagedThreadHistory) === null || _incoming$pagedThread3 === void 0 ? void 0 : _incoming$pagedThread3.hasMore,
          results: mergedResults
        }),
        sensitiveAttachmentsMetadata: Object.assign({}, existing.sensitiveAttachmentsMetadata, incoming.sensitiveAttachmentsMetadata)
      };
    }
  }
};
const getTicketThread = async variables => {
  const {
    ticketId,
    threadId,
    offset
  } = variables;
  const searchParams = new URLSearchParams();
  if (offset) {
    searchParams.set('offsetTimestampMaybe', offset);
  }
  const cacheKey = `${ticketId}/${threadId}/${offset}`;
  const existing = threadIdToState.get(cacheKey);
  const response = await api(`${TICKET_THREAD_VIEW_BASE_PATH}/${ticketId}/${threadId}?${searchParams.toString()}`, existing !== null && existing !== void 0 && existing.etag ? {
    headers: {
      'If-None-Match': existing.etag
    }
  } : undefined);
  if (response.status === 304) {
    return existing === null || existing === void 0 ? void 0 : existing.data;
  }
  const data = await response.json();
  const etag = response.headers.get('Etag');
  if (etag) {
    threadIdToState.set(cacheKey, {
      etag,
      data
    });
  }
  return data;
};
export const GET_TICKET_THREAD = registerQuery({
  fieldName: GET_TICKET_THREAD_KEY,
  args: ['ticketId', 'threadId', 'offset'],
  fetcher: getTicketThread
});
export const useTicketThread = (ticketId, threadId) => {
  const _useQuery = useQuery(GET_TICKET_THREAD, {
      variables: {
        ticketId,
        threadId
      },
      skip: !ticketId || !threadId,
      pollInterval: 5000,
      skipPollAttempt: () => document.visibilityState === 'hidden'
    }),
    {
      data,
      loading,
      refetch,
      fetchMore: _fetchMore,
      error
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  const hasMore = useMemo(() => {
    var _data$GET_TICKET_THRE, _data$GET_TICKET_THRE2;
    return (_data$GET_TICKET_THRE = data === null || data === void 0 || (_data$GET_TICKET_THRE2 = data[GET_TICKET_THREAD_KEY]) === null || _data$GET_TICKET_THRE2 === void 0 || (_data$GET_TICKET_THRE2 = _data$GET_TICKET_THRE2.pagedThreadHistory) === null || _data$GET_TICKET_THRE2 === void 0 ? void 0 : _data$GET_TICKET_THRE2.hasMore) !== null && _data$GET_TICKET_THRE !== void 0 ? _data$GET_TICKET_THRE : false;
  }, [data]);
  const offset = useMemo(() => {
    var _data$GET_TICKET_THRE3;
    return data === null || data === void 0 || (_data$GET_TICKET_THRE3 = data[GET_TICKET_THREAD_KEY]) === null || _data$GET_TICKET_THRE3 === void 0 || (_data$GET_TICKET_THRE3 = _data$GET_TICKET_THRE3.pagedThreadHistory) === null || _data$GET_TICKET_THRE3 === void 0 || (_data$GET_TICKET_THRE3 = _data$GET_TICKET_THRE3.offset) === null || _data$GET_TICKET_THRE3 === void 0 ? void 0 : _data$GET_TICKET_THRE3.timestamp;
  }, [data]);
  const fetchMore = useCallback(() => {
    if (!hasMore) {
      return;
    }
    void _fetchMore({
      variables: {
        offset
      }
    });
  }, [_fetchMore, hasMore, offset]);
  const lastMessageId = useMemo(() => {
    var _data$GET_TICKET_THRE4;
    return data === null || data === void 0 || (_data$GET_TICKET_THRE4 = data[GET_TICKET_THREAD_KEY]) === null || _data$GET_TICKET_THRE4 === void 0 || (_data$GET_TICKET_THRE4 = _data$GET_TICKET_THRE4.pagedThreadHistory) === null || _data$GET_TICKET_THRE4 === void 0 || (_data$GET_TICKET_THRE4 = _data$GET_TICKET_THRE4.results.at(-1)) === null || _data$GET_TICKET_THRE4 === void 0 ? void 0 : _data$GET_TICKET_THRE4.id;
  }, [data]);
  return Object.assign({
    data: data === null || data === void 0 ? void 0 : data[GET_TICKET_THREAD_KEY],
    hasMore,
    refetch,
    loading,
    fetchMore,
    lastMessageId,
    error
  }, rest);
};